import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'Admin' | 'Receptionist' | 'Broker';
  lastLogin?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, role: string) => Promise<void>;
  logout: () => void;
  updateProfile: (name: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Set up axios interceptors
  useEffect(() => {
    // Configure axios defaults
    // Removed withCredentials to fix CORS with n8n cloud webhook
    
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    // Response interceptor to handle token expiration
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Check if user is logged in on mount
useEffect(() => {
  const checkAuth = async () => {
    // ✅ BYPASS LOGIN IN DEVELOPMENT MODE (no backend needed)
    const mockAdminUser: User = {
      id: 'dev-admin-001',
      email: 'admin@naiminvestments.com',
      name: 'Admin User (Dev Mode - No Backend)',
      role: 'Admin',
      lastLogin: new Date().toISOString()
    };
    setUser(mockAdminUser);
    setLoading(false);
  };

  checkAuth();
}, []);

const login = async (email: string, password: string) => {
  // ✅ MOCK LOGIN FOR DEVELOPMENT (no backend needed)
  const mockAdminUser: User = {
    id: 'dev-admin-001',
    email: email,
    name: 'Admin User (Mock Login)',
    role: 'Admin',
    lastLogin: new Date().toISOString()
  };
  setUser(mockAdminUser);
};

const register = async (email: string, password: string, name: string, role: string) => {
  // ✅ MOCK REGISTRATION FOR DEVELOPMENT (no backend needed)
  const mockUser: User = {
    id: `dev-user-${Date.now()}`,
    email: email,
    name: name,
    role: role as 'Admin' | 'Receptionist' | 'Broker',
    lastLogin: new Date().toISOString()
  };
  setUser(mockUser);
};

const logout = () => {
  localStorage.removeItem('token');
  delete axios.defaults.headers.common['Authorization'];
  setUser(null);
};

const updateProfile = async (name: string) => {
  // ✅ MOCK PROFILE UPDATE FOR DEVELOPMENT (no backend needed)
  if (user) {
    setUser({ ...user, name: name });
  }
};

const value: AuthContextType = {
  user,
  loading,
  login,
  register,
  logout,
  updateProfile
};

return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
